import { createFileRoute } from "@tanstack/react-router";
import { PublicHeader, PublicFooter } from "@/components/public-layout";
import { LegalPageLayout, LegalSectionHeading } from "@/components/legal-page-layout";
import { buildLocalizedSeoLinks, getLocalizedSeoUrls } from "@/lib/seo";

export const Route = createFileRoute("/security")({
	component: Security,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/security",
		});

		return {
			meta: [
				{ title: "Security - AgentFramer | MCP-Native Media Generation for AI Agents" },
				{
					name: "description",
					content:
						"Learn how AgentFramer keeps your data and AI agent credentials safe with encryption, access controls, and secure infrastructure.",
				},
				{ property: "og:title", content: "Security - AgentFramer | MCP-Native Media Generation for AI Agents" },
				{
					property: "og:description",
					content: "How AgentFramer keeps your data and AI agent credentials safe.",
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/security",
			}),
		};
	},
});

const securitySections = [
	{ id: "security-infra", number: "01", label: "Infrastructure" },
	{ id: "security-auth", number: "02", label: "Auth & Access" },
	{ id: "security-credentials", number: "03", label: "Credentials" },
	{ id: "security-ops", number: "04", label: "Operations" },
	{ id: "security-disclosure", number: "05", label: "Disclosure" },
];

function Security() {
	return (
		<div style={{ backgroundColor: "var(--km-surface)", color: "var(--km-on-surface)", minHeight: "100svh" }}>
			<PublicHeader />
			<LegalPageLayout
				contentsLabel="Security Sections"
				description="Your AI agents run autonomously. That means the credentials, generated assets, and workspace data they interact with must be protected carefully. Here is how we secure the infrastructure."
				lastUpdated="2026.04.01.UTC"
				sections={securitySections}
				title={
					<>
						SECURITY
						<br />
						<span style={{ color: "#5A33D4" }}>ARCHITECTURE.</span>
					</>
				}
				version="Sec v1.2.0"
			>
				{/* 01 Infrastructure */}
				<section id="security-infra" className="space-y-5 scroll-mt-28">
					<LegalSectionHeading number="01" title="Infrastructure Security" />
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						{[
							{ icon: "public", title: "Global Edge", text: "AgentFramer runs on Cloudflare Workers, deployed across 300+ edge locations. All traffic is protected by Cloudflare DDoS mitigation and WAF." },
							{ icon: "storage", title: "Data Storage", text: "Persistent data is stored in isolated, encrypted databases. Media assets are stored in Cloudflare R2 with private access controls." },
							{ icon: "lock", title: "TLS Everywhere", text: "All communication between agents, our API, and generation providers is encrypted in transit using TLS 1.2 or higher." },
						].map((item) => (
							<div
								key={item.title}
								className="p-5 space-y-2"
								style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)" }}
							>
								<div className="flex items-center gap-2">
									<span className="material-symbols-outlined" aria-hidden="true" style={{ color: "#6B3FF2", fontSize: "18px" }}>{item.icon}</span>
									<span className="text-sm font-bold" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>{item.title}</span>
								</div>
								<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>{item.text}</p>
							</div>
						))}
					</div>
					<div className="flex flex-wrap gap-2">
						{["CLOUDFLARE_WORKERS", "CLOUDFLARE_R2", "TLS_1.3", "DDoS_MITIGATION", "WAF_ENABLED"].map((badge) => (
							<span key={badge} className="text-xs px-2.5 py-1" style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", background: "rgba(234,40,4,0.05)", border: "1px solid rgba(234,40,4,0.12)", borderLeft: "2px solid #6B3FF2", color: "#6B3FF2" }}>{badge}</span>
						))}
					</div>
				</section>

				{/* 02 Auth */}
				<section id="security-auth" className="space-y-5 scroll-mt-28">
					<LegalSectionHeading number="02" title="Authentication & Access" />
					<div className="space-y-2">
						{[
							{ n: "#01", label: "Secure Sessions", text: "Authentication uses Better Auth with cryptographically signed session tokens. Sessions expire automatically and are invalidated on logout." },
							{ n: "#02", label: "OAuth Provider", text: "Google OAuth sign-in is supported. We never store OAuth provider passwords, only scoped access tokens needed for authentication." },
							{ n: "#03", label: "API Keys", text: "API keys are hashed before storage. A compromised key can be revoked instantly from your dashboard without affecting other active keys." },
							{ n: "#04", label: "Workspace Isolation", text: "Each workspace is strictly isolated. Members can only access resources within workspaces they have been explicitly invited to." },
						].map((row) => (
							<div key={row.n} className="flex items-start gap-4 px-4 py-3" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.06)" }}>
								<span className="text-xs font-bold mt-0.5 shrink-0" style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#6B3FF2" }}>{row.n}</span>
								<div>
									<div className="text-sm font-bold mb-0.5" style={{ color: "var(--km-on-surface)" }}>{row.label}</div>
									<div className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>{row.text}</div>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* 03 Credentials */}
				<section id="security-credentials" className="space-y-5 scroll-mt-28">
					<LegalSectionHeading number="03" title="AI Provider Credentials" />
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<div className="p-5 space-y-2" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)" }}>
							<div className="flex items-center gap-2">
								<span className="material-symbols-outlined" aria-hidden="true" style={{ color: "#6B3FF2", fontSize: "18px" }}>encrypted</span>
								<span className="text-sm font-bold" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>Encrypted at Rest</span>
							</div>
							<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>Provider credentials are stored encrypted at rest and never exposed in API responses or logs.</p>
						</div>
						<div className="p-5 space-y-2" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)" }}>
							<div className="flex items-center gap-2">
								<span className="material-symbols-outlined" aria-hidden="true" style={{ color: "#6B3FF2", fontSize: "18px" }}>verified_user</span>
								<span className="text-sm font-bold" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>Least Privilege</span>
							</div>
							<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>AgentFramer requests only the permissions required to dispatch generation jobs. No unnecessary scopes are ever requested.</p>
						</div>
					</div>
					<div className="px-4 py-3 text-sm font-bold flex items-center gap-2" style={{ background: "rgba(234,40,4,0.04)", border: "1px solid #6B3FF2", color: "#6B3FF2" }}>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>verified</span>
						Credentials are never logged, indexed, or returned in any API response.
					</div>
				</section>

				{/* 04 Operations */}
				<section id="security-ops" className="space-y-5 scroll-mt-28">
					<LegalSectionHeading number="04" title="Operational Security" />
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						{[
							{ icon: "speed", title: "Rate Limiting", text: "All API endpoints are rate-limited via Upstash Redis to prevent abuse. Agents that exceed limits receive structured errors." },
							{ icon: "package_2", title: "Dependencies", text: "Dependencies are kept up to date and monitored for known vulnerabilities. Critical patches are applied promptly." },
							{ icon: "admin_panel_settings", title: "Access Controls", text: "Internal access to production systems is restricted to authorized team members using short-lived credentials." },
							{ icon: "monitor_heart", title: "Error Monitoring", text: "Application errors are captured with PII scrubbing enabled. No sensitive user data or credentials in logs." },
						].map((item) => (
							<div key={item.title} className="p-5 space-y-2" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)" }}>
								<div className="flex items-center gap-2">
									<span className="material-symbols-outlined" aria-hidden="true" style={{ color: "#6B3FF2", fontSize: "18px" }}>{item.icon}</span>
									<span className="text-sm font-bold" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>{item.title}</span>
								</div>
								<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>{item.text}</p>
							</div>
						))}
					</div>
				</section>

				{/* 05 Disclosure */}
				<section id="security-disclosure" className="space-y-5 scroll-mt-28">
					<LegalSectionHeading number="05" title="Vulnerability Disclosure" />
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<div className="p-5 space-y-3" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)" }}>
							<div className="text-sm font-bold" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>Responsible Disclosure</div>
							<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
								If you discover a security vulnerability, please report it responsibly. Do not open a public GitHub issue for security matters. We will respond within 48 hours.
							</p>
							<ul className="space-y-1">
								{["Describe the vulnerability clearly", "Include reproduction steps", "We will confirm receipt within 48h", "We keep reporters informed throughout"].map((right) => (
									<li key={right} className="text-xs flex items-start gap-2" style={{ color: "var(--km-on-surface-muted)" }}>
										<span className="material-symbols-outlined mt-0.5" aria-hidden="true" style={{ fontSize: "12px", color: "#6B3FF2" }}>check_circle</span>
										{right}
									</li>
								))}
							</ul>
						</div>
						<div className="p-5 space-y-3" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)" }}>
							<div className="text-sm font-bold" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>Contact</div>
							<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>Security reports and general security questions go to our security team.</p>
							<a
								href="mailto:security@agentframer.com"
								aria-label="Send email to security@agentframer.com"
								className="block px-3 py-2 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", background: "rgba(234,40,4,0.05)", border: "1px solid rgba(234,40,4,0.12)", borderLeft: "2px solid #6B3FF2", color: "#6B3FF2", textDecoration: "underline dotted #6B3FF2" }}
							>
								security@agentframer.com
							</a>
						</div>
					</div>
				</section>
			</LegalPageLayout>
			<PublicFooter />
		</div>
	);
}
